// catalogImportApi.ts — the contract module for PO/quote → catalog (039).
//
// Same convention as poApi.ts, poImportApi.ts, poScanApi.ts, quoteApi.ts and
// packingListApi.ts: every API-shape assumption for this flow lives here, read
// off api/src/procurement/catalog-import-service.js and catalog-import-ai.js.
//
// ── WHY IT LIVES UNDER Parts/ ───────────────────────────────────────────────
// The four sibling lanes' contracts sit beside the purchase-order screens
// because each ends in a purchasing document. This one ends in `parts_catalog`
// and nothing else, so it sits with the catalog. The API route is under
// /api/parts-catalog for the same reason.
//
// ── WHAT IS SHARED ──────────────────────────────────────────────────────────
// The review-entry shape and `reviewKey` come from the review kit
// (components/review/grounding), because the gate is literally the same server
// code. The holding-cell row shapes come from poScanApi, because migrations
// 018/038/039 put all four lanes in one table. Nothing else: this lane has no
// vendor, no money and no quantities.

import { useApi } from '../apiHelpers';
import type { ReviewTarget, ConfirmReviewInput } from '../components/review/grounding';
import type { ImportWarning } from '../Procurement/poImportApi';
import { draftPageUrl } from '../Procurement/poScanApi';
import type { MaterialType, CommodityClass } from '../Receiving/types';

export type { ImportWarning } from '../Procurement/poImportApi';
export { reviewKey } from '../components/review/grounding';
export type { ScanDraftSummary as CatalogDraftSummary, ScanDraftFile } from '../Procurement/poScanApi';
export { isDraftImage } from '../Procurement/poScanApi';

export type CatalogImportSource = 'catalog_pdf' | 'catalog_photo';

/** What this document wants to do about one line. */
export type CatalogLineAction =
  /** Nothing in the catalog matches. Create it. */
  | 'mint'
  /** Something matches, and it has empty columns this document can fill. */
  | 'fill'
  /** Something matches and there is nothing to add. Shown, switched off. */
  | 'complete';

/** The only columns a document is allowed to fill in on an existing row. */
export type FillableField = 'manufacturer' | 'mpn' | 'lead_time_days';

export interface CatalogFill {
  field: FillableField;
  value: string | number;
  /** The lead time exactly as printed ("16-24 weeks"), when that is the field. */
  printed: string | null;
  /**
   * TRUE only for lead_time_days: a value that is about THIS DOCUMENT rather than
   * about the item. A quote for a rush order or a quantity break carries a real
   * lead time that is not the item's normal one, and written to the catalog it
   * would look normal forever. The screen puts the warning next to this field and
   * nowhere else, which is what keeps the warning worth reading.
   */
  circumstantial: boolean;
  /** Set by the screen. False means DEFER: this field is not written. */
  apply?: boolean;
}

/** The document says one thing, the catalog already holds another. */
export interface CatalogConflict {
  field: FillableField;
  current: string | number;
  proposed: string | number;
}

export interface CatalogImportLine {
  line_number: number;
  description: string | null;
  part_number: string | null;
  manufacturer: string | null;
  /**
   * NULL and 0 are DIFFERENT ANSWERS. 0 means the document said "in stock"; null
   * means it said nothing, which is what `lead_time_days IS NULL` has to keep
   * meaning for the reorder maths.
   */
  lead_time_days: number | null;
  lead_time_text: string | null;

  // Filled by the server against the catalog as it stands.
  part_catalog_id: number | null;
  matched_sku: string | null;
  action: CatalogLineAction;
  fillable: CatalogFill[];
  conflicts?: CatalogConflict[];
  existing: {
    sku: string;
    description: string;
    manufacturer: string | null;
    mpn: string | null;
    lead_time_days: number | null;
  } | null;

  included: boolean;
  ai_not_sure: boolean;
}

export interface CatalogImportExtraction {
  document_reference: string | null;
  document_date: string | null;
  lines: CatalogImportLine[];
  line_count: number;
  /** How many lines named a manufacturer. A document naming NONE is a fact. */
  manufacturer_count: number;
  lead_time_count: number;
}

export interface CatalogImportConfidence {
  lines: number;
  manufacturer: number;
  lead_time: number;
}

export type CatalogReviewReason =
  | 'low_confidence'
  | 'unverified_text'
  | 'ungrounded_scan'
  | 'ai_remark'
  | 'model_not_sure'
  | 'duplicate_row'
  | 'lead_time_implausible'
  | 'conflicts_with_catalog';

export interface CatalogReviewEntry {
  target: ReviewTarget;
  field: string | null;
  line_number: number | null;
  reason: CatalogReviewReason;
  message: string;
}

export interface CatalogImportParseResponse {
  ok: true;
  instance: 'firefly' | 'swarm';
  source_filename: string | null;
  source: CatalogImportSource;
  extraction: CatalogImportExtraction;
  confidence: CatalogImportConfidence;
  warnings: ImportWarning[];
  counts: { mint: number; fill: number; complete: number };
  review_required: CatalogReviewEntry[];
  /** Highest numeric tail already taken per SKU stem, for the grammar. */
  sku_tail_max: Record<string, number>;
  draft_id?: number | null;
}

export const CATALOG_IMPORT_DRAFTS_PATH = '/parts-catalog/import/drafts';

/** An object URL for one page of a staged read. THE CALLER OWNS THE REVOKE. */
export const catalogDraftPageUrl = (draftId: number, page: number): Promise<string | null> =>
  draftPageUrl(draftId, page, CATALOG_IMPORT_DRAFTS_PATH);

export interface CatalogImportDraft {
  id: number;
  created_at: string;
  created_by_name: string | null;
  source: CatalogImportSource;
  vendor_guess: string | null;
  /** The document's own reference, or its filename. This lane reads no vendor. */
  po_number_guess: string | null;
  staged_doc: CatalogImportParseResponse;
  files: { page_number: number; mimetype: string; size_bytes: number }[];
}

// ---------------------------------------------------------------------------
// Confirm
// ---------------------------------------------------------------------------

export interface CatalogMintInput {
  /** `item_kinds.key`. Required on every mint: it is what the item IS, and it
   *  decides which of the next two fields the server expects. */
  kind_key: string;
  /** Required for a `short` kind, which has no other identity. Null for a
   *  board, whose name is generated from the SKU's own attributes. */
  name?: string | null;
  /** STRUCTURED KINDS ONLY. A short kind is minted with no SKU and the server
   *  assigns `STEM-XXXX`; sending one would be refused. */
  sku?: string;
  material_type: MaterialType;
  commodity_class: CommodityClass;
  description?: string | null;
  mpn?: string | null;
  manufacturer?: string | null;
  lead_time_days?: number | null;
}

export interface CatalogConfirmLineInput {
  action: CatalogLineAction;
  included?: boolean;
  /** On a mint. */
  mint?: CatalogMintInput | null;
  /** On a fill. */
  part_catalog_id?: number | null;
  fills?: CatalogFill[];
  description?: string | null;
}

export interface CatalogConfirmInput {
  document: {
    source: CatalogImportSource;
    source_filename: string | null;
    lines: CatalogConfirmLineInput[];
  };
  staged_extraction?: CatalogImportExtraction;
  staged_confidence?: CatalogImportConfidence;
  staged_warnings?: ImportWarning[];
  reviews?: ConfirmReviewInput[];
  draft_id?: number | null;
}

export interface CatalogConfirmResult {
  ok: true;
  minted: {
    line_number: number;
    part_catalog_id: number;
    sku: string;
    manufacturer: string | null;
    mpn: string | null;
    lead_time_days: number | null;
  }[];
  filled: { part_catalog_id: number; sku: string; fields: FillableField[] }[];
  /**
   * Fields somebody else filled in between the read and the save. Not an error:
   * the catalog's value was kept, and this says which, so nobody wonders why a
   * box they ticked changed nothing.
   */
  skipped: { sku: string; field: FillableField; kept: string | number }[];
  minted_count: number;
  filled_count: number;
}

export function useCatalogImportApi() {
  const api = useApi();

  return {
    /** Read the items off ONE document. A PDF, or photos of its pages in order. */
    parse: (files: File | File[]): Promise<CatalogImportParseResponse> => {
      const fd = new FormData();
      for (const file of Array.isArray(files) ? files : [files]) fd.append('file', file);
      return api.post('/parts-catalog/import/parse', { body: fd });
    },

    /** Mint the new items and fill in the empty columns of the ones we have. */
    confirm: (body: CatalogConfirmInput): Promise<CatalogConfirmResult> =>
      api.post('/parts-catalog/import/confirm', { body }),

    listDrafts: (): Promise<{ drafts: import('../Procurement/poScanApi').ScanDraftSummary[] }> =>
      api.get(CATALOG_IMPORT_DRAFTS_PATH),

    getDraft: (id: number): Promise<CatalogImportDraft> =>
      api.get(`${CATALOG_IMPORT_DRAFTS_PATH}/${id}`),

    discardDraft: (id: number): Promise<void> =>
      api.delete(`${CATALOG_IMPORT_DRAFTS_PATH}/${id}`, { skipJsonParse: true }),
  };
}
